import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { displayPhoneBR } from '../../lib/phone';

interface AdminBet {
  bet_id: string;
  round_id: number;
  status: string;
  payment_method: string | null;
  amount_cents: number;
  user_id: string;
  user_name: string | null;
  user_phone: string;
  user_pix_key: string | null;
  paid_at: string | null;
  created_at: string;
  manual_proof_url: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  awaiting_payment: 'Aguardando pagamento',
  awaiting_manual_confirmation: 'Aguardando confirmação',
  paid: 'Pago',
  cancelled: 'Cancelado',
};

const METHOD_LABEL: Record<string, string> = {
  mercado_pago: 'Mercado Pago',
  manual_pix: 'Pix manual',
};

function statusRowClass(status: string): string {
  if (status === 'paid') return 'bg-emerald-50 hover:bg-emerald-100';
  if (status === 'awaiting_payment' || status === 'awaiting_manual_confirmation')
    return 'bg-amber-50 hover:bg-amber-100';
  if (status === 'draft') return 'bg-slate-100 hover:bg-slate-200';
  if (status === 'cancelled') return 'bg-red-50 hover:bg-red-100';
  return '';
}

export default function AdminBetsPage() {
  const qc = useQueryClient();
  const [roundFilter, setRoundFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data } = useQuery({
    queryKey: ['admin-bets', roundFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (roundFilter) params.set('round_id', roundFilter);
      if (statusFilter) params.set('status', statusFilter);
      return (await api.get<AdminBet[]>(`/admin/bets?${params}`)).data;
    },
  });

  const confirmManual = useMutation({
    mutationFn: async (betId: string) =>
      (await api.post(`/admin/bets/${betId}/confirm-manual-pix`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-bets'] }),
  });

  const forceMarkPaid = useMutation({
    mutationFn: async (betId: string) =>
      (await api.post(`/admin/bets/${betId}/force-mark-paid`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-bets'] }),
  });

  function handleForceMarkPaid(b: AdminBet) {
    const ok = confirm(
      `Confirmar manualmente o pagamento do bolão de ${b.user_name || displayPhoneBR(b.user_phone)} (R${b.round_id})?\n\nUse só se você verificou o pagamento por fora (extrato, MP, etc).`,
    );
    if (ok) forceMarkPaid.mutate(b.bet_id);
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Bolões</h2>

      <div className="flex gap-2 text-sm">
        <select className="input w-40" value={roundFilter} onChange={(e) => setRoundFilter(e.target.value)}>
          <option value="">Todas rodadas</option>
          <option value="1">Rodada 1</option>
          <option value="2">Rodada 2</option>
          <option value="3">Rodada 3</option>
        </select>
        <select className="input w-56" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Todos status</option>
          <option value="draft">Rascunho</option>
          <option value="awaiting_payment">Aguardando pagamento</option>
          <option value="awaiting_manual_confirmation">Aguardando confirmação</option>
          <option value="paid">Pago</option>
        </select>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2 px-2">Usuário</th>
              <th className="px-2">Celular</th>
              <th className="px-2">Pix</th>
              <th className="px-2">Rodada</th>
              <th className="px-2">Status</th>
              <th className="px-2">Método</th>
              <th className="px-2">Valor</th>
              <th className="px-2">Criado</th>
              <th className="px-2">Pago</th>
              <th className="px-2">Ação</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((b) => {
              const isPaid = b.status === 'paid' || b.status === 'cancelled';
              return (
                <tr key={b.bet_id} className={`border-b ${statusRowClass(b.status)}`}>
                  <td className="py-2 px-2">{b.user_name || '—'}</td>
                  <td className="text-xs whitespace-nowrap px-2">{displayPhoneBR(b.user_phone)}</td>
                  <td className="text-xs px-2">
                    {b.user_pix_key ? (
                      <span className="font-mono">{b.user_pix_key}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-2">R{b.round_id}</td>
                  <td className="px-2 font-medium">{STATUS_LABEL[b.status] || b.status}</td>
                  <td className="px-2">{b.payment_method ? METHOD_LABEL[b.payment_method] || b.payment_method : '—'}</td>
                  <td className="px-2 whitespace-nowrap">R$ {(b.amount_cents / 100).toFixed(2)}</td>
                  <td className="text-xs px-2">{new Date(b.created_at).toLocaleString('pt-BR')}</td>
                  <td className="text-xs px-2">
                    {b.paid_at ? new Date(b.paid_at).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="px-2">
                    <div className="flex flex-col gap-1">
                      {b.status === 'awaiting_manual_confirmation' && (
                        <button
                          className="btn btn-primary text-xs py-1"
                          onClick={() => confirmManual.mutate(b.bet_id)}
                          disabled={confirmManual.isPending}
                        >
                          Confirmar Pix
                        </button>
                      )}
                      {!isPaid && (
                        <button
                          className="btn btn-secondary text-xs py-1"
                          onClick={() => handleForceMarkPaid(b)}
                          disabled={forceMarkPaid.isPending}
                          title="Marcar como pago manualmente (use se o webhook do MP falhou)"
                        >
                          Forçar pago
                        </button>
                      )}
                      {b.manual_proof_url && (
                        <a className="text-xs underline" href={b.manual_proof_url} target="_blank" rel="noreferrer">
                          Ver comprovante
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
